import { useState } from 'react';
import { EnvelopeSimple, X, PaperPlaneRight, WhatsappLogo, User, CheckCircle } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const CONTACT_EMAIL = 'greg@trackmypackage.app';

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const handleSend = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    const subject = encodeURIComponent(`Track My Package Inquiry from ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.open(`mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`, '_self');
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ name: '', email: '', message: '' }); }, 4000);
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-20 right-5 z-50 w-14 h-14 rounded-full bg-[#0077FF] text-white shadow-lg shadow-[#0077FF]/30 flex items-center justify-center hover:bg-[#0066DD] transition-colors"
            data-testid="chat-widget-toggle"
          >
            <EnvelopeSimple size={28} weight="fill" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-5 z-50 w-[360px] bg-[#0A1628] border border-white/10 rounded-2xl shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#003166] to-[#0077FF] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <EnvelopeSimple size={18} className="text-white" weight="fill" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Get in Touch</p>
                  <p className="text-white/60 text-[10px]">We'll reply within 24 hours</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chat-close-btn">
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <div className="p-4">
              {sent ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                  <CheckCircle size={48} className="text-emerald-400 mx-auto mb-3" weight="fill" />
                  <p className="text-white font-bold text-sm">Email client opened!</p>
                  <p className="text-gray-400 text-xs mt-1">Just hit send in your email app.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSend} className="space-y-2.5">
                  <input
                    type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your name" required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#0077FF]"
                    data-testid="chat-lead-name"
                  />
                  <input
                    type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com" required
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#0077FF]"
                    data-testid="chat-lead-email"
                  />
                  <textarea
                    value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    placeholder="How can we help you?" required rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#0077FF] resize-none"
                    data-testid="chat-message-input"
                  />
                  <button type="submit"
                    className="w-full bg-[#FF9900] hover:bg-[#FF9900]/90 text-white rounded-lg py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    data-testid="chat-send-btn"
                  >
                    <PaperPlaneRight size={16} weight="fill" /> Send Email
                  </button>
                </form>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 pb-3 flex gap-2">
              <a href="https://wa.me/50586156364" target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 bg-[#25D366]/15 text-[#25D366] rounded-lg py-2 text-xs font-medium hover:bg-[#25D366]/25 transition-colors"
                data-testid="chat-whatsapp-btn">
                <WhatsappLogo size={16} weight="fill" /> WhatsApp Us
              </a>
              <a href="https://sqawk.app/book/MrPositivity" target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 bg-[#FF9900]/15 text-[#FF9900] rounded-lg py-2 text-xs font-medium hover:bg-[#FF9900]/25 transition-colors"
                data-testid="chat-book-call-btn">
                <User size={16} weight="fill" /> Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
